import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Spinner } from 'reactstrap';

interface Props {
  label: string;
  maxFiles?: number;
  isUploading?: boolean;
  progress?: { done: number; total: number } | null;
  // Si devuelve false (falló) se conservan los previews para reintentar
  onUpload: (files: File[]) => void | boolean | Promise<void | boolean>;
}

/** Zona de subida con previews en miniatura y quitar individual. */
const PropertyPhotoUploader = ({
  label,
  maxFiles,
  isUploading = false,
  progress = null,
  onUpload,
}: Props) => {
  const [files, setFiles] = useState<Array<File & { preview: string }>>([]);

  const onDrop = useCallback(
    (accepted: File[]) => {
      const withPreview = accepted.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) }),
      );
      setFiles((current) => {
        if (maxFiles === 1) return withPreview.slice(0, 1);
        const next = [...current, ...withPreview];
        return maxFiles ? next.slice(0, maxFiles) : next;
      });
    },
    [maxFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles,
    onDrop,
    disabled: isUploading,
  });

  // libera las URLs de preview al desmontar
  useEffect(
    () => () => files.forEach((file) => URL.revokeObjectURL(file.preview)),
    [],
  );

  const handleUpload = async () => {
    const ok = await onUpload(files);
    if (ok !== false) setFiles([]);
  };

  const removeFile = (target: File) =>
    setFiles((current) => current.filter((file) => file !== target));

  return (
    <div>
      <div
        {...getRootProps({
          className: `border rounded text-center px-3 py-4 ${
            isDragActive ? 'bg-secondary' : ''
          }`,
          style: {
            borderStyle: 'dashed',
            cursor: isUploading ? 'default' : 'pointer',
          },
        })}
      >
        <input {...getInputProps()} />
        <i className="fa-solid fa-cloud-arrow-up fa-lg text-primary mb-2 d-block" />
        <strong className="d-block">{label}</strong>
        <small style={{ color: '#525f7f' }}>
          Arrastra {maxFiles === 1 ? 'tu foto' : 'tus fotos'} o haz clic para
          seleccionar. Formatos: JPEG y PNG.
        </small>
      </div>

      {files.length > 0 && (
        <>
          <div className="d-flex flex-wrap mt-3">
            {files.map((file) => (
              <div
                key={file.preview}
                className="position-relative mr-2 mb-2"
                style={{ width: 72, height: 72 }}
              >
                <img
                  alt=""
                  src={file.preview}
                  className="rounded"
                  style={{ width: 72, height: 72, objectFit: 'cover' }}
                />
                {!isUploading && (
                  <button
                    type="button"
                    aria-label="Quitar foto"
                    className="btn btn-sm btn-danger position-absolute p-0 rounded-circle"
                    style={{ top: -6, right: -6, width: 20, height: 20 }}
                    onClick={() => removeFile(file)}
                  >
                    <i className="fa-solid fa-xmark" style={{ fontSize: 10 }} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="d-flex align-items-center justify-content-between">
            <Button
              type="button"
              color="link"
              size="sm"
              className="px-0 text-muted"
              disabled={isUploading}
              onClick={() => setFiles([])}
            >
              Quitar todas
            </Button>
            <Button
              type="button"
              color="primary"
              size="sm"
              disabled={isUploading}
              onClick={handleUpload}
            >
              {isUploading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {progress
                    ? `Subiendo ${progress.done} de ${progress.total}...`
                    : 'Subiendo...'}
                </>
              ) : (
                <>
                  <i className="fa-solid fa-upload mr-2" />
                  Subir {files.length} {files.length === 1 ? 'foto' : 'fotos'}
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PropertyPhotoUploader;
